import { useEffect, useState } from 'react';
import { Crosshair } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface ForecastSummary {
  commit: number;
  bestCase: number;
  pipeline: number;
  accuracy: number | null;
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Story 56.5 — realistic weekly forecast. Three numbers (commit / best
 * case / pipeline) plus a calibration metric so the user can see whether
 * the team's commits historically held up.
 */
export function ForecastSummaryWidget() {
  const [data, setData] = useState<ForecastSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<ForecastSummary>('/analytics/forecast-summary')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading forecast…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Crosshair className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Forecast (this period)
        </h3>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-md border-l-4 border-emerald-500 bg-emerald-950/40/50 px-3 py-2">
          <div className="text-xs uppercase text-emerald-200">Commit</div>
          <div className="mt-1 text-lg font-bold">{fmt(data.commit)}</div>
        </div>
        <div className="rounded-md border-l-4 border-amber-500 bg-amber-950/40/50 px-3 py-2">
          <div className="text-xs uppercase text-amber-200">Best case</div>
          <div className="mt-1 text-lg font-bold">{fmt(data.bestCase)}</div>
        </div>
        <div className="rounded-md border-l-4 border-border bg-card px-3 py-2">
          <div className="text-xs uppercase text-foreground">Pipeline</div>
          <div className="mt-1 text-lg font-bold">{fmt(data.pipeline)}</div>
        </div>
      </div>
      <div className="mt-3 border-t pt-3 text-sm text-muted-foreground">
        {data.accuracy !== null ? (
          <>
            Historical accuracy:{' '}
            <span className="font-semibold text-foreground">
              {(Number(data.accuracy) * 100).toFixed(0)}%
            </span>{' '}
            <span className="text-xs">
              (won-since vs commit from 30 days ago)
            </span>
          </>
        ) : (
          <span className="text-xs">
            Calibration unavailable — daily forecast snapshots are needed for at least 30 days.
          </span>
        )}
      </div>
    </div>
  );
}
